import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authApi, AuthedUser } from './api';
import { clearSessionToken, getSessionToken, setSessionToken, subscribeSessionToken } from './sessionToken';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

interface AuthState {
  token: string | null;
  user: AuthedUser | null;
  permissions: string[];
  loading: boolean;           // true while bootstrapping from stored token
  bootstrapped: boolean | null;  // null = unknown, true/false after /status
}

interface AuthApi extends AuthState {
  login: (identifier: string, password: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  bootstrap: (body: { secret: string; username: string; email: string; password: string; displayName?: string }) =>
    Promise<{ ok: true } | { ok: false; error: string }>;
  logout: () => Promise<void>;
  hasPermission: (id: string) => boolean;
  refreshStatus: () => Promise<void>;
}

const Ctx = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    token: null,
    user: null,
    permissions: [],
    loading: true,
    bootstrapped: null,
  });

  useEffect(() => subscribeSessionToken(token => {
    setState(previous => previous.token === token ? previous : {
      ...previous, token: null, user: null, permissions: [], loading: false,
    });
  }), []);

  const refreshStatus = useCallback(async () => {
    // Network error ⇒ bootstrapped stays null and the UI shows the
    // "connecting to backend" state. Never let this throw up into the
    // useEffect below or loading gets stuck.
    try {
      const r = await authApi.status();
      setState((s) => ({ ...s, bootstrapped: r.ok ? r.data.bootstrapped : null }));
    } catch {
      setState((s) => ({ ...s, bootstrapped: null }));
    }
  }, []);

  // On mount, try the saved token. If /me accepts it, we're logged in.
  // Otherwise clear and show login.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await refreshStatus();
        if (!active) return;
        // Token handed off from the Architect app in the URL fragment
        // (#token=…, workspace switch) — adopt it and strip the hash
        // before the normal restore path runs.
        // DESIGN47 — a one-shot sign-in code from the mini app (#handoff=…):
        // redeem it for a session of our own, then strip the hash.
        const h = /[#&]handoff=([^&]+)/.exec(window.location.hash || '');
        if (h) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          try {
            const r = await fetch(API_BASE + '/api/auth/handoff/redeem', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code: decodeURIComponent(h[1]) }),
            });
            if (r.ok) {
              const data = await r.json();
              if (active && data && data.token) setSessionToken(data.token);
            }
          } catch { /* fall through to the saved session, if any */ }
        }
        const m = /[#&]token=([^&]+)/.exec(window.location.hash || '');
        if (m) {
          let t = m[1];
          try { t = decodeURIComponent(t); } catch { /* keep raw */ }
          setSessionToken(t);
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        if (!active) return;
        const saved = getSessionToken();
        if (!saved) return;
        const r = await authApi.me(saved);
        if (!active || getSessionToken() !== saved) return;
        if (r.ok) {
          setSessionToken(saved);
          setState((s) => ({
            ...s,
            token: saved,
            user: r.data.user,
            permissions: r.data.permissions || [],
          }));
        } else if (r.status === 401 || r.status === 403) {
          clearSessionToken(saved);
        }
      } catch {
        // Network down — fall through to "show login" so the user
        // can at least see the form rather than an infinite spinner.
      } finally {
        if (active) setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => { active = false; };
  }, [refreshStatus]);

  // Root can change stage access while a colleague has the workspace open.
  // Refresh capabilities without replacing drafts or starting a new session.
  useEffect(() => {
    if (!state.token) return;
    let cancelled = false;
    let refreshSequence = 0;
    const token = state.token;
    const refresh = async () => {
      const request = ++refreshSequence;
      try {
        const result = await authApi.me(token);
        if (cancelled || request !== refreshSequence || getSessionToken() !== token) return;
        if (result.ok) {
          setState(previous => previous.token !== token ? previous : { ...previous, user: result.data.user, permissions: result.data.permissions || [] });
        } else if (result.status === 401 || result.status === 403) {
          // A verified revocation must also remove already-open protected UI.
          // A network failure or 5xx keeps the session/drafts for a retry.
          clearSessionToken(token);
          setState(previous => previous.token !== token ? previous : { ...previous, token: null, user: null, permissions: [], loading: false });
        }
      } catch { /* The server checks access on every request; retry on focus. */ }
    };
    void refresh();
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void refresh(); }, 30000);
    return () => { cancelled = true; window.removeEventListener('focus', refresh); window.clearInterval(timer); };
  }, [state.token]);

  const login: AuthApi['login'] = async (identifier, password) => {
    const r = await authApi.login({ identifier, password });
    if (!r.ok) return { ok: false, error: r.data.message || r.data.error || 'login failed' };
    setSessionToken(r.data.token);
    setState((s) => ({
      ...s,
      token: r.data.token,
      user: r.data.user,
      permissions: r.data.permissions || [],
      bootstrapped: true,
    }));
    return { ok: true };
  };

  const bootstrap: AuthApi['bootstrap'] = async (body) => {
    const r = await authApi.bootstrap(body);
    if (!r.ok) return { ok: false, error: r.data.message || r.data.error || 'bootstrap failed' };
    setSessionToken(r.data.token);
    setState((s) => ({
      ...s,
      token: r.data.token,
      user: r.data.user,
      permissions: r.data.permissions || [],
      bootstrapped: true,
    }));
    return { ok: true };
  };

  const logout = async () => {
    const t = state.token;
    // Optimistic UI; if the call fails we still clear local state —
    // the token is gone from storage so subsequent requests won't
    // send it, and the old session will naturally expire on the server.
    setState({ token: null, user: null, permissions: [], loading: false, bootstrapped: state.bootstrapped });
    if (t) clearSessionToken(t);
    if (t) await authApi.logout(t).catch(() => {});
  };

  const hasPermission = (id: string) => !!state.user?.isRoot || state.permissions.includes(id);

  return (
    <Ctx.Provider value={{ ...state, login, bootstrap, logout, hasPermission, refreshStatus }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}

// Convenience wrapper for authed fetches to the backend. Routes the
// Authorization header and auto-logs-out on 401 so stale tokens don't
// linger after server-side revocation.
export function useAuthedFetch() {
  const { token } = useAuth();
  return useCallback(
    async (input: RequestInfo, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      if (token) headers.set('Authorization', `Bearer ${token}`);
      const res = await fetch(input, { ...init, headers });
      if (res.status === 401 && token) {
        // Server says this token is no longer valid — drop it.
        clearSessionToken(token);
      }
      return res;
    },
    [token],
  );
}

// Exposed so non-hook code (the shared API helper in AssignHub etc.)
// can attach the header too. Shares the active session with the interceptor.
export function currentAuthHeader(): Record<string, string> {
  const t = getSessionToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}
